/**
 * The custom redirect domain — a CNAME somebody has to go and create, and the
 * gate click tracking sits behind.
 *
 * ## Why it exists at all, in one sentence a person can act on
 *
 * Without a redirect subdomain the tenant owns, Amazon rewrites every link in
 * every message through `awstrack.me`. To the reader of a mail from their
 * bank, every link now hovers as an unfamiliar third-party address — which is
 * the textbook description of a phishing mail, and is read that way by the
 * person *and* by the filter. Click tracking is refused until this verifies,
 * and the refusal says that rather than saying "not available".
 *
 * That sentence is not written here. It comes from
 * `TRACKING_DISCLOSURE.clickRequiresDomain` in `@wend/shared`, so the copy on
 * this panel, the copy on the refused switch and the message the API logs when
 * it refuses the write are all the same sentence.
 *
 * ## The shape is DKIM's, because a CNAME verification is a CNAME verification
 *
 * The brief for this slice says to copy the DKIM flow this screen already
 * handles rather than invent a new one. It turns out this screen does **not**
 * handle DKIM — nothing in `apps/web`, `apps/api` or `packages/*` mentions it,
 * because slice 3 leaves identity verification to the AWS console and reports
 * the result. So the pattern is copied from the shape a DKIM flow has anywhere,
 * and from the nearest thing this repository actually does have, which is the
 * SES check card next door: show the exact record in the three fields a
 * registrar's form has, make each one copyable, re-check on demand, and never
 * turn "not propagated yet" into an error.
 *
 * The report for this slice carries that discrepancy. Whoever builds DKIM
 * should copy *this*, and at that point the two want to be one component.
 *
 * ## Four states, worded as four different situations
 *
 * `not_configured` is a settled decision, not an outstanding task: a tenant who
 * never wants click tracking lives here for ever and gets no warning triangle.
 * `pending` is a setup step in progress. `verified` is done. `broken` is an
 * **incident** — links in mail already delivered now point at a host that does
 * not resolve — and it is the only red on this screen. Collapsing `broken` into
 * `pending` would show "DNS changes take a few minutes" to somebody whose live
 * campaign links are dead, which is the specific failure the four-armed union in
 * `@wend/shared` exists to prevent.
 *
 * ## The record is displayed, never computed
 *
 * `record.value` arrives on the wire. The host depends on the AWS region, AWS
 * has moved its tracking hosts before, and it is in no SDK type — so a frontend
 * that derived it from a string constant would be a frontend handing somebody a
 * DNS record that silently does nothing, and doing it confidently. This panel
 * renders `record.name`, `record.type` and `record.value` exactly as given and
 * has no fallback for their absence, because there is no honest fallback.
 */
import {
  trackingRedirectDomainRequestSchema,
  type TrackingCnameRecord,
  type TrackingResponse,
} from '@wend/shared';
import { Badge, Button, Callout, FormField, Input, Spinner } from '@wend/ui';
import { RotateCw, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { ROOT_FIELD, apiFieldErrors } from '../../lib/api/errors';
import { formatDateTime } from '../../lib/format';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import { FormAlert } from '../../components/FormAlert';
import { CopyableValue } from './CopyableValue';
import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';
import {
  TRACKING_DEFAULT_REDIRECT_HOST,
  TRACKING_DOMAIN_FAILURE_HEADING,
  domainFailureNeedsAction,
  readRedirectDomain,
  trackingDomainFailureMessage,
} from './tracking-display';
import { checkRedirectDomain, clearRedirectDomain, requestRedirectDomain } from './tracking-api';

export interface RedirectDomainPanelProps {
  readonly response: TrackingResponse;
  readonly onChanged: (next: TrackingResponse) => void;
  /** True while another control on the card is writing. */
  readonly busy?: boolean;
}

/**
 * This panel is rendered only once the card's preconditions hold — an SES
 * account is connected and Wend has provisioned the configuration set — so it
 * carries no "not connected" branch of its own. Adding one would be a second,
 * unreachable statement of a refusal that already has one place to live, and an
 * unreachable branch is a branch nobody notices going stale.
 */
export function RedirectDomainPanel({
  response,
  onChanged,
  busy = false,
}: RedirectDomainPanelProps) {
  const state = response.settings.redirectDomain;
  const display = readRedirectDomain(state);
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [pending, setPending] = useState<'check' | 'clear' | null>(null);
  const [replacing, setReplacing] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);

  async function run(kind: 'check' | 'clear', action: () => Promise<TrackingResponse>) {
    if (pending !== null) return;
    setFailure(null);
    setPending(kind);
    try {
      onChanged(await action());
      setConfirmingClear(false);
      setReplacing(false);
    } catch (error) {
      setFailure(requestFailure(error));
    } finally {
      setPending(null);
    }
  }

  return (
    <section className="flex flex-col gap-4 border-t border-line pt-5">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-[0.98rem] font-medium text-text">Redirect subdomain</h3>
        <Badge tone={display.badge.tone}>{display.badge.label}</Badge>
        {busy && <Spinner size="sm" label="Saving" />}
      </div>

      <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
        {response.disclosure.clickRequiresDomain}
      </p>

      {failure !== null && <RequestFailureAlert failure={failure} />}

      {state.state === 'not_configured' || replacing ? (
        <DomainForm
          existing={state.state === 'not_configured' ? null : state.domain}
          onDone={(next) => {
            onChanged(next);
            setReplacing(false);
          }}
          {...(state.state === 'not_configured' ? {} : { onCancel: () => { setReplacing(false); } })}
        />
      ) : (
        <>
          {/*
            The heading and tone come from the state, not from the failure, so
            `broken` reads as an incident even when its reason is the same
            `record_not_found` a calm `pending` would carry.
          */}
          <Callout
            tone={display.tone ?? 'info'}
            title={display.title}
            // Announced only when this render followed a check the person just
            // ran. A panel that loads already broken is met in reading order.
            live="off"
          >
            {state.state === 'verified' ? (
              <p>
                Amazon is rewriting links through{' '}
                <span className="font-mono text-[0.85rem] text-text">{state.domain}</span> rather
                than {TRACKING_DEFAULT_REDIRECT_HOST}. Verified{' '}
                {formatDateTime(state.verifiedAt)}. Leave the CNAME record in place — removing it
                breaks every link in mail that has already gone out.
              </p>
            ) : state.state === 'broken' ? (
              <>
                <p>
                  <span className="font-mono text-[0.85rem] text-text">{state.domain}</span>{' '}
                  verified on {formatDateTime(state.verifiedAt)} and stopped resolving. Every link
                  in every message sent while it was working points through it, so those links are
                  broken now — for recipients who have not opened the mail yet as well as for those
                  who have. This is the CNAME record that has to come back.
                </p>
                <p className="mt-2 font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                  Last checked {formatDateTime(state.lastCheckedAt)}
                </p>
              </>
            ) : (
              <>
                <p>
                  Create this record with whoever hosts DNS for{' '}
                  <span className="font-mono text-[0.85rem] text-text">{state.domain}</span>. Click
                  tracking stays off until it resolves — which is the point of the requirement, not
                  a delay in it.
                </p>
                <p className="mt-2 font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                  {state.lastCheckedAt === null
                    ? `Requested ${formatDateTime(state.requestedAt)}. Not checked yet.`
                    : `Last checked ${formatDateTime(state.lastCheckedAt)}`}
                </p>
              </>
            )}
          </Callout>

          {/*
            The reason is shown separately from the state, because the two
            answer different questions: the state says how bad this is, the
            reason says what to do next. `record_not_found` under a `broken`
            heading and under a `pending` heading want the same instruction and
            a completely different urgency.
          */}
          {(state.state === 'pending' || state.state === 'broken') && (
            <Callout
              tone={domainFailureNeedsAction(state.reason) ? 'warn' : 'info'}
              title={TRACKING_DOMAIN_FAILURE_HEADING[state.reason]}
            >
              {trackingDomainFailureMessage(state.reason)}
            </Callout>
          )}

          {display.showsRecord && <CnameRecord record={state.record} />}

          <div className="flex flex-wrap items-center gap-3">
            {display.offersCheck && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={pending === 'check'}
                disabled={busy || pending !== null}
                leadingIcon={<RotateCw aria-hidden="true" />}
                onClick={() => {
                  void run('check', checkRedirectDomain);
                }}
              >
                {pending === 'check' ? 'Checking DNS' : 'Check now'}
              </Button>
            )}

            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy || pending !== null}
              onClick={() => {
                setReplacing(true);
              }}
            >
              Use a different subdomain
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy || pending !== null}
              leadingIcon={<Trash2 aria-hidden="true" />}
              onClick={() => {
                setConfirmingClear(true);
              }}
            >
              Remove
            </Button>

            {display.offersCheck && (
              <p className="text-[0.85rem] text-muted">
                Wend re-checks on its own too. This asks now.
              </p>
            )}
          </div>

          {confirmingClear && (
            <div className="flex flex-col gap-3 rounded-base border border-bad/25 bg-white/[0.02] px-4 py-3.5">
              {/*
                What is lost, not "are you sure". Two consequences, and the
                second one is the one nobody expects: Wend has no access to the
                tenant's DNS and cannot tidy up after itself.
              */}
              <p className="max-w-prose text-[0.92rem] leading-relaxed text-text">
                Removing <span className="font-mono text-[0.88rem]">{state.domain}</span> switches
                click tracking off — there is no state where the subdomain is gone and
                click tracking survives, because Amazon would immediately go back to rewriting your
                links through {TRACKING_DEFAULT_REDIRECT_HOST}. Wend cannot delete the CNAME record
                itself; that is in your DNS and you will want to remove it there too, or it stays in
                your zone pointing at an Amazon host that no longer routes for this account.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={pending === 'clear'}
                  className="border-bad/45 text-bad hover:border-bad/70 hover:bg-bad/10"
                  onClick={() => {
                    void run('clear', clearRedirectDomain);
                  }}
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                  {pending === 'clear' ? 'Removing' : 'Yes, remove it and turn click tracking off'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={pending !== null}
                  onClick={() => {
                    setConfirmingClear(false);
                  }}
                >
                  Keep it
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* The record                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The DNS record, in the three fields a registrar's form actually has.
 *
 * Laid out as name / type / value / TTL rather than as one string, because
 * nobody pastes a CNAME as one string — every DNS console has three or four
 * boxes and this is the panel somebody has open in the other tab. TTL is
 * labelled "suggested" because registrars disagree about defaults and some do
 * not offer the field at all; nothing depends on the value.
 *
 * Each row is a {@link CopyableValue}, which is where the copy button and its
 * failure behaviour live — and which is now shared with the SNS topic panel on
 * this same card. The reasoning for why a copy control must never be the only
 * way to get a value out is in that file's header rather than restated here,
 * because two copies of an argument are two things to keep in step and this one
 * has a subtle conclusion.
 *
 * Type and TTL carry no copy button: nobody needs `CNAME` on a clipboard, and
 * two more tab stops per row is a real cost to somebody working through this
 * with a keyboard.
 */
function CnameRecord({ record }: { record: TrackingCnameRecord }) {
  return (
    <div className="rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
      <h4 className="font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted">
        The record to create
      </h4>
      <p className="mt-1.5 max-w-prose text-[0.85rem] leading-relaxed text-muted">
        Amazon supplied this for your region. Copy it exactly — several DNS providers append your
        domain to whatever you type in the name field, which turns{' '}
        <span className="font-mono text-[0.8rem] text-text">{record.name}</span> into{' '}
        <span className="font-mono text-[0.8rem] text-text">
          {record.name}.{record.name.split('.').slice(1).join('.')}
        </span>{' '}
        and is the single most common reason this never verifies.
      </p>

      <dl className="mt-3 flex flex-col">
        <CopyableValue label="Name" value={record.name} />
        <CopyableValue label="Type" value={record.type} copyable={false} />
        <CopyableValue label="Value" value={record.value} />
        <CopyableValue label="TTL (suggested)" value={String(record.ttlSeconds)} copyable={false} />
      </dl>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Claiming a subdomain                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The one input in this flow, validated against the shared contract before it
 * leaves the browser.
 *
 * `trackingRedirectDomainSchema` refuses a bare apex — at least three labels —
 * because a CNAME cannot coexist with other records at a zone apex, so pointing
 * `acmeretail.in` at Amazon would either be refused by their DNS provider or
 * take their website down, and the second is an outcome Wend must not be able
 * to cause by accepting the input.
 *
 * It does **not** catch `acmebank.co.in`, which is three labels and still an
 * apex, and no public-suffix list is shipped to make it — that is a decision
 * recorded in `@wend/shared`, on the grounds that a bundled PSL goes stale and
 * the tenant's own DNS provider refuses that case with a better message than
 * Wend could write. Which is exactly why the field's guidance says *use a
 * subdomain such as `links.` or `email.`* rather than relying on the regex to
 * teach it. Guidance is the honest tool here; the pattern is a backstop.
 */
function DomainForm({
  existing,
  onDone,
  onCancel,
}: {
  existing: string | null;
  onDone: (next: TrackingResponse) => void;
  onCancel?: () => void;
}) {
  const [domain, setDomain] = useState(existing ?? '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setFailure(null);

    const result = validate(trackingRedirectDomainRequestSchema, { domain });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      // `result.data.domain` rather than the raw input: the contract trims and
      // lower-cases, and sending the untransformed string would let the browser
      // and the API disagree about what was asked for by a trailing space.
      onDone(await requestRedirectDomain(result.data));
    } catch (error) {
      const fieldErrors = apiFieldErrors(error);
      delete fieldErrors[ROOT_FIELD];
      setErrors(fieldErrors);
      setFailure(requestFailure(error));
      setPending(false);
    }
  }

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      noValidate
      className="flex max-w-xl flex-col gap-4"
    >
      {existing !== null && (
        <Callout tone="warn" title="This replaces the subdomain you are using now">
          The CNAME record for{' '}
          <span className="font-mono text-[0.85rem] text-text">{existing}</span> stops being used
          the moment this is saved, and Wend cannot remove it from your DNS. If click tracking is
          on, it goes off until the new subdomain verifies.
        </Callout>
      )}

      {failure !== null && <RequestFailureAlert failure={failure} />}
      {errors[ROOT_FIELD] !== undefined && <FormAlert tone="error">{errors[ROOT_FIELD]}</FormAlert>}

      <FormField
        label="Redirect subdomain"
        required
        description="A subdomain you control, on a domain your recipients already recognise — links.yourbank.in or email.yourbank.in. Not the bare domain: a CNAME cannot sit at the top of a zone, and pointing your apex at Amazon would take your website down."
        error={errors.domain}
      >
        <Input
          value={domain}
          onChange={(event) => {
            setDomain(event.target.value);
            setErrors((current) => withoutError(current, 'domain'));
          }}
          autoComplete="off"
          spellCheck={false}
          placeholder="links.yourbank.in"
          className="font-mono text-[0.9rem]"
          disabled={pending}
        />
      </FormField>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" loading={pending}>
          {pending ? 'Saving' : 'Save and show me the DNS record'}
        </Button>

        {onCancel !== undefined && (
          <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
        )}

        <p className="text-[0.85rem] text-muted">
          Nothing changes for your recipients until the record verifies and you switch click
          tracking on.
        </p>
      </div>
    </form>
  );
}
